import { Outlet } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { Tabs } from '@/features/layout/components/Tabs'
import { BackgroundTaskIndicator } from '@/shared/components/BackgroundTaskIndicator'
import { BackgroundTaskModal } from '@/shared/components/BackgroundTaskModal'
import { DiscoveryResultModal } from '@/features/tiles/components/DiscoveryResultModal'
import { CommandPalette } from '@/shared/components/CommandPalette'
import { useCommandPalette } from '@/shared/hooks/useCommandPalette'
import { useKeyboardShortcuts } from '@/shared/hooks/useKeyboardShortcuts'
import { ChatPanel } from '@/features/chat'
import { FloatingChatButton } from '@/shared/components/FloatingChatButton'
import { useChatPanel } from '@/shared/hooks/useMobileMenu'
import { useNetworkStatus } from '@/shared/hooks/useNetworkStatus'
import { useToast } from '@/shared/hooks/useToast'
import { useGlobalEventStream } from '@/shared/hooks/useGlobalEventStream'
import { getUserEmail } from '@/shared/lib/auth'

export function Root() {
  const commandPalette = useCommandPalette()
  const { isChatOpen, closeChat, isFullscreen, side } = useChatPanel()
  const { isOnline } = useNetworkStatus()
  const { showToast } = useToast()
  const hasMounted = useRef(false)

  // Single persistent SSE connection — routes all streaming events to domain dispatchers
  useGlobalEventStream(getUserEmail())

  // Register keyboard shortcuts
  useKeyboardShortcuts()

  // Prevent body scroll when chat is fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.classList.add('no-scroll')
    } else {
      document.body.classList.remove('no-scroll')
    }
    return () => { document.body.classList.remove('no-scroll') }
  }, [isFullscreen])

  // Handle network status changes
  useEffect(() => {
    // Skip toast on initial mount
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }

    if (!isOnline) {
      showToast('You are offline. Changes may not sync.', 'error')
    } else {
      showToast('Back online', 'success')
    }
  }, [isOnline, showToast])

  return (
    <div
      className="h-dvh flex flex-col overflow-hidden bg-canvas text-foreground font-sans tracking-tight selection:bg-accent selection:text-white"
    >
      <Tabs />
      <div className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </div>
      <BackgroundTaskIndicator />
      <BackgroundTaskModal />
      <DiscoveryResultModal />
      <CommandPalette isOpen={commandPalette.isOpen} onClose={commandPalette.close} />

      {/* Chat Overlay Backdrop */}
      {isChatOpen && !isFullscreen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 sm:bg-transparent sm:backdrop-blur-none sm:pointer-events-none"
          onClick={closeChat}
          aria-hidden="true"
        />
      )}

      {/* Chat Panel - Fixed overlay */}
      <div
        className={`
          fixed top-0 bottom-0 z-50 overflow-hidden
          transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${isFullscreen
            ? `!z-[9999] inset-x-0 bg-[#0a0a0f] shadow-none ${isChatOpen ? 'translate-x-0' : side === 'right' ? 'translate-x-full' : '-translate-x-full'}`
            : `shadow-2xl shadow-black/50
               max-sm:inset-x-0
               ${side === 'right'
                 ? `sm:border-l border-white/5 ${isChatOpen ? 'translate-x-0' : 'max-sm:translate-x-full sm:translate-x-full'}`
                 : `sm:border-r border-white/5 ${isChatOpen ? 'translate-x-0' : 'max-sm:-translate-x-full sm:-translate-x-full'}`
               }`
          }
          ${side === 'right' ? 'right-0' : 'left-0'}
        `}
        // On mobile (< 640px / sm breakpoint), width is controlled by max-sm:inset-x-0 (full width).
        // On desktop, use clamp so the panel scales between 500px and 960px.
      >
        <div
          className={
            isFullscreen
              ? 'h-full w-full'
              : 'h-full max-sm:w-full sm:w-[clamp(500px,50vw,960px)]'
          }
        >
          <ChatPanel />
        </div>
      </div>

      {/* Floating Chat Button */}
      {!isChatOpen && <FloatingChatButton />}
    </div>
  )
}
